const DATABASE = 'marksure-marking-recovery';
const VERSION = 1;
const controllers = new Map();
const documentIds = new WeakMap();
let sequence = 0;

function hasRemainingMarking(state) {
  if (state?.batchMode === false) return !state.marking;
  const items = state?.batchItems || [];
  return !items.length || items.length < (state?.examFiles?.length || 0)
    || items.some(item => !item?.marking && item?.status !== 'skipped');
}

export function getMarkingDocumentId(file) {
  if (!file) return null;
  if (!documentIds.has(file)) documentIds.set(file, `document-${Date.now()}-${++sequence}-${Math.random().toString(36).slice(2)}`);
  return documentIds.get(file);
}

export function getMarkingDraftOwner(apiUrl = '') {
  const user = localStorage.getItem('user_id');
  const school = localStorage.getItem('school_id');
  return user && school ? `${apiUrl}|school:${school}|user:${user}` : null;
}

export function encodeMarkingDraft(state) {
  const documents = new Map();
  const encode = value => {
    if (typeof Blob !== 'undefined' && value instanceof Blob) {
      const id = getMarkingDocumentId(value);
      documents.set(id, { id, blob: value, name: value.name || 'script', type: value.type, lastModified: value.lastModified || 0 });
      return { __marksureDocument: id };
    }
    if (Array.isArray(value)) return value.map(encode);
    if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, encode(item)]));
    return value;
  };
  return { state: encode(state), documents: [...documents.values()] };
}

export function decodeMarkingDraft(state, documents) {
  const files = new Map();
  for (const document of documents) {
    const file = new File([document.blob], document.name, { type: document.type, lastModified: document.lastModified });
    documentIds.set(file, document.id); files.set(document.id, file);
  }
  const decode = value => {
    if (value?.__marksureDocument && Object.keys(value).length === 1) {
      if (!files.has(value.__marksureDocument)) throw new Error('A saved script could not be recovered.');
      return files.get(value.__marksureDocument);
    }
    if (Array.isArray(value)) return value.map(decode);
    if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, decode(item)]));
    return value;
  };
  return decode(state);
}

function openDatabase() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') { reject(new Error('This browser cannot save marking recovery.')); return; }
    const request = indexedDB.open(DATABASE, VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains('drafts')) db.createObjectStore('drafts', { keyPath: 'owner' });
      if (!db.objectStoreNames.contains('documents')) {
        const store = db.createObjectStore('documents', { keyPath: 'key' }); store.createIndex('owner', 'owner');
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('Marking recovery storage could not be opened.'));
    request.onblocked = () => reject(new Error('Close older MarkSure tabs to enable marking recovery.'));
  });
}

export const markingDraftStorage = {
  async load(owner) {
    const db = await openDatabase();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['drafts', 'documents'], 'readonly');
      const record = transaction.objectStore('drafts').get(owner);
      const documents = transaction.objectStore('documents').index('owner').getAll(owner);
      transaction.oncomplete = () => { db.close(); resolve(record.result ? { ...record.result, documents: documents.result } : null); };
      transaction.onabort = transaction.onerror = () => { db.close(); reject(transaction.error || new Error('Saved marking could not be read.')); };
    });
  },
  async save(owner, record, documents) {
    const db = await openDatabase();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['drafts', 'documents'], 'readwrite');
      transaction.objectStore('drafts').put({ ...record, owner, version: VERSION });
      for (const document of documents) transaction.objectStore('documents').put({ ...document, key: `${owner}|${document.id}`, owner });
      const keep = new Set(record.documentIds || []);
      const cursor = transaction.objectStore('documents').index('owner').openCursor(owner);
      cursor.onsuccess = () => { if (cursor.result) { if (!keep.has(cursor.result.value.id)) cursor.result.delete(); cursor.result.continue(); } };
      transaction.oncomplete = () => { db.close(); resolve(); };
      transaction.onabort = transaction.onerror = () => { db.close(); reject(transaction.error || new Error('There is not enough browser storage to save this marking.')); };
    });
  },
  async clear(owner) {
    const db = await openDatabase();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['drafts', 'documents'], 'readwrite');
      transaction.objectStore('drafts').delete(owner);
      const cursor = transaction.objectStore('documents').index('owner').openCursor(owner);
      cursor.onsuccess = () => { if (cursor.result) { cursor.result.delete(); cursor.result.continue(); } };
      transaction.oncomplete = () => { db.close(); resolve(); };
      transaction.onabort = transaction.onerror = () => { db.close(); reject(transaction.error || new Error('The recovery copy could not be cleared.')); };
    });
  },
};

export function createMarkingDraftController(owner, storage = markingDraftStorage) {
  const listeners = new Set(); const writtenDocuments = new Set();
  let ready; let queue = Promise.resolve(); let revision = 0; let runCounter = 0;
  const controller = { owner, state: null, status: 'loading', error: '', savedAt: null, activeRun: null, generation: 0, memoRequests: new Map(),
    subscribe(listener) { listeners.add(listener); return () => listeners.delete(listener); },
    notify(reason) { listeners.forEach(listener => listener(controller, reason)); },
    load() {
      if (ready) return ready.then(() => controller.state);
      ready = storage.load(owner).then(record => {
        if (record) {
          if (record.version !== VERSION || record.owner !== owner) throw new Error('The saved marking uses an incompatible recovery format.');
          controller.state = decodeMarkingDraft(record.state, record.documents || []);
          for (const document of record.documents || []) writtenDocuments.add(document.id);
          if (controller.state.inProgress && !controller.activeRun) {
            const state = controller.state;
            controller.state = { ...state, inProgress: false, interrupted: hasRemainingMarking(state) };
          }
          controller.savedAt = record.savedAt;
        }
        controller.status = record ? 'saved' : 'ready'; controller.notify('loaded'); return controller.state;
      }).catch(error => { controller.status = 'error'; controller.error = error.message; controller.notify('status'); return null; });
      return ready;
    },
    patch(patch, reason = 'ui') {
      controller.state = { ...(controller.state || {}), ...(typeof patch === 'function' ? patch(controller.state || {}) : patch) };
      controller.notify(reason); return controller.flush();
    },
    flush() {
      if (!controller.state) return queue;
      const encoded = encodeMarkingDraft(controller.state); const currentRevision = ++revision; const savedAt = new Date().toISOString();
      controller.status = 'saving'; controller.notify('status');
      queue = queue.catch(() => {}).then(async () => {
        await storage.save(owner, { state: encoded.state, savedAt, documentIds: encoded.documents.map(document => document.id) }, encoded.documents.filter(document => !writtenDocuments.has(document.id)));
        writtenDocuments.clear();
        encoded.documents.forEach(document => writtenDocuments.add(document.id));
        controller.savedAt = savedAt;
        if (currentRevision === revision) { controller.status = 'saved'; controller.error = ''; controller.notify('status'); }
      }).catch(error => { controller.status = 'error'; controller.error = error.message; controller.notify('status'); });
      return queue;
    },
    beginRun(snapshot) {
      if (controller.activeRun) return null;
      const run = { id: ++runCounter, abort: new AbortController() }; controller.activeRun = run;
      controller.patch({ ...snapshot, inProgress: true, interrupted: false }, 'job'); return run;
    },
    patchRun(run, patch) { if (controller.activeRun === run) return controller.patch(patch, 'job'); return Promise.resolve(); },
    finishRun(run, patch = {}) {
      if (controller.activeRun !== run) return Promise.resolve();
      controller.activeRun = null; return controller.patch({ ...patch, inProgress: false }, 'job');
    },
    cancelRun() {
      const run = controller.activeRun; if (!run) return;
      run.abort.abort(); controller.activeRun = null;
      controller.patch({ inProgress: false, interrupted: hasRemainingMarking(controller.state) }, 'job');
    },
    async clear() {
      controller.generation += 1; controller.cancelRun(); controller.memoRequests.clear(); await queue;
      await storage.clear(owner); controller.state = null; writtenDocuments.clear();
      controller.savedAt = null; controller.status = 'ready'; controller.error = ''; controller.notify('cleared');
    },
  };
  return controller;
}

export function getMarkingDraftController(owner) {
  if (!owner) return null;
  if (!controllers.has(owner)) controllers.set(owner, createMarkingDraftController(owner));
  return controllers.get(owner);
}

export function cancelMarkingRecoveryJobs() { controllers.forEach(controller => controller.cancelRun()); }

if (typeof window !== 'undefined') window.addEventListener('storage', event => {
  if (['user_id', 'school_id', 'auth_token', 'isAuthenticated'].includes(event.key) || event.key === null) cancelMarkingRecoveryJobs();
});
